import { useEffect, useState, useRef, useCallback } from "react";
import {
  getAdminReports,
  getAllEmployees,
  getAllProjects,
} from "../../service/manager_service";
import AdminDataTable from "../../components/admin/AdminDataTable";
import CustomLoadingindicator from "../../components/custom_loading";
import { CustomSearchableSelect } from "../../components/admin/AdminFields";
import { toast } from "react-toastify";
import {
  Calendar,
  Download,
  ChevronDown,
} from "lucide-react";
import "./AdminReports.css";

function AdminReports() {
  const [initialLoading, setInitialLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);

  const [reports, setReports] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);

  const currentYearStr = new Date().getFullYear().toString();
  const currentMonthStr = (new Date().getMonth() + 1).toString();

  // Filters
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [filterMonth, setFilterMonth] = useState(currentMonthStr);
  const [filterYear, setFilterYear] = useState(currentYearStr);

  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const monthDropdownRef = useRef(null);
  const yearDropdownRef = useRef(null);

  const months = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const currentYear = parseInt(currentYearStr);
  const years = Array.from({ length: 5 }, (_, i) =>
    (currentYear - 2 + i).toString(),
  );

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        monthDropdownRef.current &&
        !monthDropdownRef.current.contains(event.target)
      ) {
        setIsMonthDropdownOpen(false);
      }
      if (
        yearDropdownRef.current &&
        !yearDropdownRef.current.contains(event.target)
      ) {
        setIsYearDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchReportsData = useCallback(
    async ({ employeeid, projectid, workdate, month, year }) => {
      try {
        const res = await getAdminReports({
          employeeid: employeeid || null,
          projectid: projectid || null,
          workdate: workdate || null,
          month: month || null,
          year: year || null,
        });
        if (res.status === "success") {
          setReports(res.data.map((r, i) => ({ ...r, sno: i + 1 })));
        }
      } catch (e) {
        toast.error("Failed to fetch reports");
      }
    },
    [],
  );

  const fetchInitialData = useCallback(async () => {
    setInitialLoading(true);
    try {
      const [empRes, projRes] = await Promise.all([
        getAllEmployees(),
        getAllProjects(),
      ]);

      if (empRes.status === "success") setEmployees(empRes.data);
      if (projRes.status === "success") setProjects(projRes.data);

      await fetchReportsData({
        employeeid: null,
        projectid: null,
        workdate: null,
        month: currentMonthStr,
        year: currentYearStr,
      });
    } catch (e) {
      toast.error("Failed to initialize report data");
    } finally {
      setInitialLoading(false);
    }
  }, [currentMonthStr, currentYearStr, fetchReportsData]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setIsTableLoading(true);
    await fetchReportsData({
      employeeid: employeeFilter,
      projectid: projectFilter,
      workdate: dateFilter,
      month: filterMonth,
      year: filterYear,
    });
    setIsTableLoading(false);
  };

  const handleReset = async () => {
    setEmployeeFilter("");
    setProjectFilter("");
    setDateFilter("");
    setFilterMonth(currentMonthStr);
    setFilterYear(currentYearStr);
    setIsTableLoading(true);
    await fetchReportsData({
      employeeid: null,
      projectid: null,
      workdate: null,
      month: currentMonthStr,
      year: currentYearStr,
    });
    setIsTableLoading(false);
  };

  const handleDownload = () => {
    if (reports.length === 0) {
      toast.warn("No data available to download");
      return;
    }

    const headers = [
      "S.No",
      "Employee",
      "Project",
      "Module",
      "Task",
      "Work Date",
      "Hours",
    ];
    const csvRows = reports.map((row) => {
      const value =
        parseFloat(
          row.worked_hours || row.workinghours || row.total_worked_hours,
        ) || 0;
      const hPart = Math.floor(value);
      const mPart = Math.round((value - hPart) * 100);
      const totalMinutes = hPart * 60 + mPart;
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      const formattedHours = `${hours}h ${minutes < 10 ? "0" + minutes : minutes}m`;

      return [
        row.sno,
        `"${(row.employeename || row.employee_name || "").replace(/"/g, '""')}"`,
        `"${(row.projectname || "").replace(/"/g, '""')}"`,
        `"${(row.modulename || "").replace(/"/g, '""')}"`,
        `"${(row.taskname || "").replace(/"/g, '""')}"`,
        row.date || row.workdate || "",
        `"${formattedHours}"`,
      ].join(",");
    });

    const csvContent = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `All_Work_Reports_${new Date().toLocaleDateString()}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Excel report downloaded successfully");
  };

  const columns = [
    { key: "sno", label: "S.No" },
    { key: "actions", label: "View" },
    {
      key: "employeename",
      label: "Employee",
      render: (val, row) => (
        <div style={{ fontWeight: 600, color: "#0f172a" }}>
          {val || row.employee_name}
        </div>
      ),
    },
    { key: "projectname", label: "Project" },
    { key: "modulename", label: "Module" },
    {
      key: "projecttaskname",
      label: "Assigned Task",
      render: (val) => val || <span style={{ color: "#94a3b8", fontStyle: "italic" }}>No specific task</span>
    },
    {
      key: "taskname",
      label: "Work Notes",
      render: (val) => <div className="description-text">{val}</div>
    },
    {
      key: "workdate",
      label: "Work Date",
      render: (val) => val ? new Date(val).toLocaleDateString("en-GB") : "N/A",
    },
    { key: "starttime", label: "Start Time" },
    { key: "endtime", label: "End Time" },
    {
      key: "workinghours",
      label: "Hours",
      render: (val) => {
        const value = parseFloat(val) || 0;
        const hPart = Math.floor(value);
        const mPart = Math.round((value - hPart) * 100);
        const totalMinutes = hPart * 60 + mPart;
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return (
          <div className="hours-badge">
            {`${hours}h ${minutes < 10 ? "0" + minutes : minutes}m`}
          </div>
        );
      },
    },
  ];

  if (initialLoading) return <CustomLoadingindicator />;

  return (
    <div className="add-report-container">
      <h4>Work Reports Directory</h4>

      <div className="add-report-card">
        <div className="admin-header-section" style={{ marginBottom: "20px" }}>
          <h5
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              margin: 0,
            }}
          >
            Filter Reports
          </h5>
          <button
            className="download-btn-header"
            onClick={handleDownload}
            title="Download Excel Report"
          >
            <Download size={16} /> Download CSV
          </button>
        </div>

        <form onSubmit={handleSubmit} className="add-report-form">
          <CustomSearchableSelect
            label="Employee"
            options={employees.map((e) => ({
              value: e.employeeid,
              label: e.employeename,
            }))}
            value={employeeFilter}
            onChange={(e) => setEmployeeFilter(e.target.value)}
            placeholder="All Employees"
          />

          <CustomSearchableSelect
            label="Project"
            options={projects.map((p) => ({
              value: p.projectid,
              label: p.projectname,
            }))}
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            placeholder="All Projects"
          />

          <div className="report-form-group">
            <label>Work Date</label>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            />
          </div>

          <div className="report-form-group">
            <label>Month</label>
            <div
              className={`month-picker-container dropdown ${isMonthDropdownOpen ? "active" : ""}`}
              onClick={() => setIsMonthDropdownOpen(!isMonthDropdownOpen)}
              ref={monthDropdownRef}
            >
              <div className="month-display">
                <Calendar size={18} className="month-icon" />
                <span>
                  {months.find((m) => m.value === filterMonth)?.label ||
                    "All Months"}
                </span>
              </div>
              <ChevronDown
                size={14}
                className={`dropdown-arrow ${isMonthDropdownOpen ? "open" : ""}`}
              />
              {isMonthDropdownOpen && (
                <div className="month-grid-dropdown">
                  <div className="month-grid-layout">
                    {months.map((m) => (
                      <div
                        key={m.value}
                        className={`month-grid-cell ${filterMonth === m.value ? "active" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilterMonth(m.value);
                          setIsMonthDropdownOpen(false);
                        }}
                      >
                        {m.label.substring(0, 3)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="report-form-group">
            <label>Year</label>
            <div
              className={`month-picker-container dropdown ${isYearDropdownOpen ? "active" : ""}`}
              onClick={() => setIsYearDropdownOpen(!isYearDropdownOpen)}
              ref={yearDropdownRef}
            >
              <div className="month-display">
                <Calendar size={18} className="month-icon" />
                <span>{filterYear || "All Years"}</span>
              </div>
              <ChevronDown
                size={14}
                className={`dropdown-arrow ${isYearDropdownOpen ? "open" : ""}`}
              />
              {isYearDropdownOpen && (
                <div className="month-grid-dropdown">
                  <div className="month-grid-layout">
                    {years.map((y) => (
                      <div
                        key={y}
                        className={`month-grid-cell ${filterYear === y ? "active" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilterYear(y);
                          setIsYearDropdownOpen(false);
                        }}
                      >
                        {y}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
        <div
          className="submit-buttons"
          style={{
            marginTop: "24px",
            paddingTop: "20px",
            borderTop: "1px solid #f1f5f9",
          }}
        >
          <button
            type="submit"
            className="apply-btn"
            disabled={isTableLoading}
            onClick={handleSubmit}
          >
            {isTableLoading ? "Filtering..." : "Search"}
          </button>
          <button type="button" className="reset-btn" onClick={handleReset}>
            Reset
          </button>
        </div>
      </div>

      <AdminDataTable
        columns={columns}
        data={reports}
        loading={isTableLoading}
        showHeader={false}
      />
      <div style={{ height: "40px" }} />
    </div>
  );
}

export default AdminReports;
